var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { startOfDay, endOfDay } from 'date-fns';
import qs from 'qs';
import { deliveryRequest as api } from '../../api';
import { useDeliveryRequestColumns } from '../../hooks/useColumns';
import { useRequest } from '../../hooks/requestHook';
import useFetch from '../../hooks/useFetch';
import MaterialTable from '../MaterialTable';
import { decamelize } from '../../utils/camelize';
import WarehouseFilter from '../molecules/WarehouseFilter';
import DateRangeFilter from '../molecules/DateRangeFilter';
import ImportDeliveryRequestsButton from '../organisms/ImportDeliveryRequestsButton';
import PageLayout from '../atoms/PageLayout';
import { Box } from '@mui/material';
import DownloadDeliveryRequestsCSVButton from '../organisms/DownloadDeliveryRequestsCSVButton';
import Button from '../atoms/BaseButton';
var AdminDeliveryRequestsPage = function () {
    var t = useTranslation().t;
    var navigate = useNavigate();
    var location = useLocation();
    var DeliveryRequestColumns = useDeliveryRequestColumns();
    var getAll = useRequest(api.getAll);
    var urlQuery = qs.parse(location.search.slice(1));
    var initialPage = parseInt((urlQuery.page || '')) || 0;
    var pageSize = parseInt((urlQuery.pageSize || '')) || 25;
    var tableRef = React.useRef();
    var reload = React.useCallback(function () {
        if (!tableRef.current)
            return;
        tableRef.current.onQueryChange();
    }, [tableRef]);
    var defaultFilter = React.useCallback(function (field) {
        var filters = urlQuery.filters || {};
        return filters[field];
    }, [urlQuery]);
    var renderWarehouseFilter = React.useCallback(function (props) { return React.createElement(WarehouseFilter, __assign({}, props)); }, []);
    var renderDateFilter = React.useCallback(function (props) { return React.createElement(DateRangeFilter, __assign({}, props)); }, []);
    var defaultSort = React.useCallback(function (field) {
        var orderBy = urlQuery.orderBy;
        var orderDirection = urlQuery.orderDirection;
        var direction = 'asc';
        if (['asc', 'desc'].includes(orderDirection !== null && orderDirection !== void 0 ? orderDirection : ''))
            direction = orderDirection;
        if (field == orderBy)
            return direction;
    }, [urlQuery]);
    var columns = React.useMemo(function () { return [
        __assign(__assign({}, DeliveryRequestColumns.id), { filtering: false, filterCellStyle: {
                minWidth: 0,
            }, defaultSort: defaultSort('id') }),
        __assign(__assign({}, DeliveryRequestColumns.roasterCompany), { defaultFilter: defaultFilter('roaster.company'), defaultSort: defaultSort('roaster.company') }),
        __assign(__assign({}, DeliveryRequestColumns.roasterStoreName), { defaultFilter: defaultFilter('roaster.storeName'), defaultSort: defaultSort('roaster.storeName') }),
        __assign(__assign({}, DeliveryRequestColumns.roasterLastname), { defaultFilter: defaultFilter('roaster.lastname'), defaultSort: defaultSort('roaster.lastname') }),
        __assign(__assign({}, DeliveryRequestColumns.roasterFirstname), { defaultFilter: defaultFilter('roaster.firstname'), defaultSort: defaultSort('roaster.firstname') }),
        __assign(__assign({}, DeliveryRequestColumns.status), { defaultFilter: defaultFilter('status'), defaultSort: defaultSort('status') }),
        {
            field: 'warehouseNames',
            title: t('params.warehouse.name'),
            filterComponent: renderWarehouseFilter,
            defaultFilter: defaultFilter('warehouseNames'),
            sorting: false,
        },
        __assign(__assign({}, DeliveryRequestColumns.nameWithKana), { filtering: false, sorting: false }),
        __assign(__assign({}, DeliveryRequestColumns.countryCallingCode), { filtering: false, sorting: false }),
        __assign(__assign({}, DeliveryRequestColumns.phone), { filtering: false, sorting: false }),
        __assign(__assign({}, DeliveryRequestColumns.postalCode), { filtering: false, sorting: false }),
        __assign(__assign({}, DeliveryRequestColumns.fullAddress), { filtering: false, sorting: false }),
        __assign(__assign({}, DeliveryRequestColumns.requestDate), { filterComponent: renderDateFilter, defaultFilter: defaultFilter('requestDate'), defaultSort: defaultSort('requestDate') }),
        __assign(__assign({}, DeliveryRequestColumns.createdAt), { filterComponent: renderDateFilter, defaultFilter: defaultFilter('createdAt'), defaultSort: defaultSort('createdAt') }),
        __assign(__assign({}, DeliveryRequestColumns.updatedAt), { filterComponent: renderDateFilter, defaultFilter: defaultFilter('updatedAt'), defaultSort: defaultSort('updatedAt') }),
        {
            field: 'lastEditedBy.role',
            title: t('params.deliveryRequest.lastEditedBy.role'),
            lookup: {
                admin: 'Admin',
                roaster: 'Roaster',
            },
            sorting: false,
            filtering: true,
            defaultFilter: defaultFilter('lastEditedBy.role'),
        },
        {
            field: 'lastEditedBy.email',
            title: t('params.deliveryRequest.lastEditedBy.email'),
            sorting: false,
            filtering: false,
        },
    ]; }, [
        defaultFilter,
        DeliveryRequestColumns,
        renderWarehouseFilter,
        t,
        defaultSort,
        renderDateFilter,
    ]);
    var buildFilterParams = React.useCallback(function (query) {
        var q = {};
        query.filters.forEach(function (filter) {
            switch (filter.column.field) {
                case 'status':
                    q.with_status = filter.value;
                    break;
                case 'warehouseNames':
                    q.in_warehouse = filter.value;
                    break;
                case 'createdAt':
                    if (filter.value.startedAt) {
                        q.created_at_gteq = startOfDay(new Date(filter.value.startedAt));
                    }
                    if (filter.value.endedAt) {
                        q.created_at_lteq = endOfDay(new Date(filter.value.endedAt));
                    }
                    break;
                case 'updatedAt':
                    if (filter.value.startedAt) {
                        q.updated_at_gteq = startOfDay(new Date(filter.value.startedAt));
                    }
                    if (filter.value.endedAt) {
                        q.updated_at_lteq = endOfDay(new Date(filter.value.endedAt));
                    }
                    break;
                case 'requestDate':
                    if (filter.value.startedAt) {
                        q.request_date_gteq = new Date(filter.value.startedAt);
                    }
                    if (filter.value.endedAt) {
                        q.request_date_lteq = new Date(filter.value.endedAt);
                    }
                    break;
                case 'lastEditedBy.role':
                    q.with_last_edited_by_role = filter.value;
                    break;
                default:
                    if (filter.column.field) {
                        var field = filter.column.field
                            .split('.')
                            .map(function (e) { return decamelize(e); })
                            .join('_');
                        q["".concat(field, "_cont")] = filter.value;
                    }
                    break;
            }
        });
        return q;
    }, []);
    var buildOrder = React.useCallback(function (order) {
        switch (order) {
            case 'roaster.company':
                return 'roasters.company';
            case 'roaster.storeName':
                return 'roasters.store_name';
            case 'roaster.lastname':
                return 'roasters.lastname';
            case 'roaster.firstname':
                return 'roasters.firstname';
            default:
                return decamelize(order);
        }
    }, []);
    var fetchDeliveryRequests = useFetch({
        buildFilterParams: buildFilterParams,
        api: getAll,
        buildOrder: buildOrder,
    });
    var csvParams = React.useMemo(function () {
        if (!urlQuery.filters)
            return;
        var filters = Object.entries(urlQuery.filters).map(function (_a) {
            var field = _a[0], value = _a[1];
            return ({
                column: { field: field },
                operator: '=',
                value: value,
            });
        });
        return { q: buildFilterParams({ filters: filters }) };
    }, [urlQuery.filters, buildFilterParams]);
    return (React.createElement("div", null,
        React.createElement(PageLayout, { title: t('pages.deliveryRequests.title'), actions: React.createElement(Box, { display: "inline-flex" },
                React.createElement(Button, { onClick: function () { return navigate('/admin/delivery_requests/new'); } }, t('pages.newDeliveryRequest.title')),
                React.createElement(Box, { m: 1 }),
                React.createElement(DownloadDeliveryRequestsCSVButton, { params: csvParams }),
                React.createElement(Box, { m: 1 }),
                React.createElement(ImportDeliveryRequestsButton, { onComplete: reload })) },
            React.createElement(MaterialTable, { tableRef: tableRef, model: "deliveryRequest", columns: columns, data: fetchDeliveryRequests, detailPath: function (deliveryRequest) {
                    return "/admin/delivery_requests/".concat(deliveryRequest.id);
                }, options: {
                    filtering: true,
                    filterCellStyle: {
                        minWidth: 100,
                    },
                    paging: true,
                    initialPage: initialPage,
                    pageSize: pageSize,
                } }))));
};
export default AdminDeliveryRequestsPage;
